import { useReducer, useState, type Reducer } from 'react'

import { Box, Filters, Icon, Table, Text, useToggle } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import audits from 'modules/audit_log/store/collections/audits'
import type { AuditLogAccess } from 'modules/audit_log/pages/settings/AuditLogPage'

import Footer from './_Footer'
import Options from './_Options'
import EventRow from './_Row/row'
import type { Audit, State } from './constants'
import {
  BECOME_CONTEXT,
  DATE_FROM,
  DATE_TO,
  EVENT_TYPE,
  PAGE_SIZE,
  STREAM_MODEL,
  TOTAL_SPAN,
  getTitles,
} from './constants'

interface Props {
  streamModel?: string
  streamId?: string
  accesses: AuditLogAccess[]
}

interface IconProps {
  direction: 'left' | 'right'
}

interface MoveToNextPage {
  type: 'MOVE_TO_NEXT_PAGE'
  events: Audit[]
}

interface MoveToPreviousPage {
  type: 'MOVE_TO_PREVIOUS_PAGE'
  events: Audit[]
}

interface ApplyFilters {
  type: 'APPLY_FILTERS'
  form: Form
}

interface FetchSuccessful {
  type: 'FETCH_SUCCESSFUL'
  eventsData: {
    audits: Audit[]
    newest_audit: Audit | null
    has_next_page: boolean
  }
}

type Action = MoveToNextPage | MoveToPreviousPage | ApplyFilters | FetchSuccessful

const reducer: Reducer<State, Action> = (state: State, action: Action) => {
  switch (action.type) {
    case 'MOVE_TO_NEXT_PAGE':
      return {
        ...state,
        fromId: action.events && action.events[PAGE_SIZE - 1]?.id,
        direction: 'forward',
        isPreviousPage: false,
        pageChanged: true,
        loading: true,
      }

    case 'MOVE_TO_PREVIOUS_PAGE':
      return {
        ...state,
        fromId: action.events && action.events[0]?.id,
        direction: 'backward',
        pageChanged: true,
        isPreviousPage: true,
        loading: true,
      }

    case 'APPLY_FILTERS':
      const isDateFilter = action.form.get('date_from').value || action.form.get('date_to').value

      return {
        ...state,
        applyFilters: true,
        loading: true,
        direction: isDateFilter ? 'forward' : 'backward',
        isPreviousPage: false,
        fromId: null,
      }

    case 'FETCH_SUCCESSFUL':
      return {
        ...state,
        loading: false,
        pageChanged: false,
        eventsData: action.eventsData,
        applyFilters: false,
        isFirstLoad: false,
        isPreviousPage: false,
      }
  }

  action satisfies never // Exhaustivity check
}

const PageMoveIcon = ({ direction }: IconProps) => {
  return (
    <Box justifyContent="center" alignItems="center" paddingX="s16">
      <Icon.Small icon={`chevron-${direction}`} />
    </Box>
  )
}

const initialState: State = {
  fromId: null,
  direction: 'backward',
  isPreviousPage: false,
  pageChanged: false,
  loading: true,
  eventsData: {
    audits: [],
    newest_audit: null,
    has_next_page: false,
  },
  applyFilters: false,
  isFirstLoad: true,
}

const AuditTable = ({ streamModel, streamId, accesses }: Props) => {
  const { has_become_enabled } = useFeatures(['become_enabled'])
  const [eventList, setEventList] = useState([])
  const form = useLazyRef(
    () =>
      new Form(
        {
          date_from: null,
          date_to: null,
          access_id: null,
          event_type: null,
        },
        {
          date_from: 'date',
          date_to: 'date',
          access_id: 'string',
          event_type: 'string',
        }
      )
  ).current

  const fetchEvents = async () => {
    const api = ApiClient()
    const result = await api.get('/audit_log/audits/events_list')
    setEventList(result)
  }

  useAsyncData(fetchEvents, [])

  const [state, dispatch] = useReducer(reducer, initialState)

  const {
    fromId,
    pageChanged,
    loading,
    isPreviousPage,
    direction,
    eventsData,
    applyFilters,
    isFirstLoad,
  } = state

  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { audits: events, newest_audit: newestEvent, has_next_page: hasNextPage } = eventsData

  function getFormValue(type: string) {
    if (!form.get(type).isDirty) return null

    const formValue = form.get(type).value

    switch (type) {
      case EVENT_TYPE:
        return [formValue]

      case DATE_FROM:
      case DATE_TO:
        return dateUIToBackend(formValue)

      default:
        return null
    }
  }

  const titles = getTitles(has_become_enabled)
  const isFirstPage = () => (events[0] && newestEvent ? events[0].id === newestEvent.id : true)
  const filteredEvents = () => events.filter((event) => event.context !== BECOME_CONTEXT)

  function getAuditPayload() {
    const variablePayload = {
      from_event_id: fromId,
      direction: direction,
      previous_page: isPreviousPage,
      access_id: form.serialized.access_id,
      event_type: getFormValue(EVENT_TYPE),
      date_from: getFormValue(DATE_FROM),
      date_to: getFormValue(DATE_TO),
    }

    const defaultPayLoad = {
      ...variablePayload,
      stream_id: companyId,
      stream_model: STREAM_MODEL,
    }

    if (!streamId || !streamModel) return defaultPayLoad

    return {
      ...variablePayload,
      stream_id: streamId,
      stream_model: streamModel,
    }
  }

  const [childPopoverOpen, setChildPopoverOpen] = useState(false)
  const [open, toggle] = useToggle(false)
  const pageShouldReload = pageChanged || applyFilters || isFirstLoad

  useEffect(() => {
    const loadData = async () => {
      const data = getAuditPayload()
      const eventsData = await audits.fetch({ data })

      dispatch({
        type: 'FETCH_SUCCESSFUL',
        eventsData,
      })
    }

    if (pageShouldReload) loadData()
  }, [pageChanged, applyFilters]) // eslint-disable-line react-hooks/exhaustive-deps

  const FiltersOptions = () => (
    <Options
      filtersForm={form}
      onChildPopoverOpen={setChildPopoverOpen}
      eventList={eventList}
      accesses={accesses}
    />
  )

  const RenderFooter = () => (
    <Footer
      onApplyFilters={() =>
        dispatch({
          type: 'APPLY_FILTERS',
          form,
        })
      }
      toggle={toggle}
      filtersForm={form}
    />
  )

  const TableOptions = () => (
    <Box flexDirection="row" gap="s8" flexGrow alignItems="center">
      <Filters
        label={i18n.t('complaints.table.filters.label')}
        title={i18n.t('complaints.table.filters.title')}
        description={i18n.t('complaints.table.filters.description')}
        options={FiltersOptions}
        footer={RenderFooter}
        attachment="middle right"
        disableOutsideClick={childPopoverOpen}
        openFromWithin={open}
        toggleFromWithin={toggle}
      />
    </Box>
  )

  return (
    <Box>
      <Table options={TableOptions} fullWidth>
        <Table.Head>
          <Table.Row>
            {titles.map((title) => (
              <Table.HeadCell colSpan={title.colSpan} key={title.name}>
                {title.name}
              </Table.HeadCell>
            ))}
          </Table.Row>
        </Table.Head>
        {loading && <Table.Loading colSpan={TOTAL_SPAN} />}
        {!loading && !filteredEvents().length && (
          <Table.Blank
            colSpan={TOTAL_SPAN}
            text={i18n.t('settings.audit_log.filters.no_logs_found')}
          />
        )}
        {!loading && (
          <Table.Body>
            {filteredEvents().map((event) => (
              <EventRow event={event} key={event.id} totalSpan={TOTAL_SPAN} accesses={accesses} />
            ))}
          </Table.Body>
        )}
      </Table>
      {!loading && (
        <Box flexDirection="row" justifyContent="center">
          {!isFirstPage() && (
            <Box
              flexDirection="row"
              padding="s32"
              onClick={() => {
                return dispatch({
                  type: 'MOVE_TO_PREVIOUS_PAGE',
                  events,
                })
              }}
            >
              <PageMoveIcon direction="left" />
              <Text.Normal weight="semibold">
                {i18n.t('settings.audit_log.previous_page')}
              </Text.Normal>
            </Box>
          )}
          {hasNextPage && (
            <Box
              flexDirection="row"
              padding="s32"
              onClick={() => {
                return dispatch({
                  type: 'MOVE_TO_NEXT_PAGE',
                  events,
                })
              }}
            >
              <Text.Normal weight="semibold">{i18n.t('settings.audit_log.next_page')}</Text.Normal>
              <PageMoveIcon direction="right" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default observer(AuditTable)
